import React, { useState, useMemo } from 'react';
import ProductCard from '../components/ProductCard';
import StoreHeader from '../components/StoreHeader';
import FilterSortBar, { SortOption } from '../components/FilterSortBar';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';
import { ShoppingBag, ChevronRight, ShieldCheck, Sparkles, Truck, Award } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'wouter';
import CartDrawer from '../components/CartDrawer';
import CheckoutModal from '../components/CheckoutModal';

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSort, setSelectedSort] = useState<SortOption>('featured');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const { products, loading } = useProducts();
  const { totalPrice, totalItems } = useCart();

  const categories = useMemo(() => {
    return Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
  }, [products]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    return products
      .filter((p) => {
        const matchesCat = selectedCategory === 'all' || p.category === selectedCategory;
        const name = p.name || '';
        const desc = p.description || '';
        const matchesSearch =
          name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          desc.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStock = !inStockOnly || (!p.isOutOfStock && (p.stock === undefined || p.stock > 0));

        return matchesCat && matchesSearch && matchesStock;
      })
      .sort((a, b) => {
        if (selectedSort === 'price-asc') return (a.price || 0) - (b.price || 0);
        if (selectedSort === 'price-desc') return (b.price || 0) - (a.price || 0);
        if (selectedSort === 'name-asc') return (a.name || '').localeCompare(b.name || '');
        if (selectedSort === 'name-desc') return (b.name || '').localeCompare(a.name || '');
        return 0;
      });
  }, [products, selectedCategory, searchQuery, inStockOnly, selectedSort]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-9 h-9 border-3 border-[#D97736]/30 border-t-[#D97736] rounded-full animate-spin"></div>
          <p className="text-[#1F1F1F] font-bold text-xs uppercase tracking-widest">Loading Spices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F1F1F] pb-28 sm:pb-32">
      {/* Store Header matching Screenshot 1 */}
      <StoreHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Hero Announcement Banner */}
      <div className="bg-[#2B2B2B] text-white py-2 px-4 text-center">
        <p className="text-[10px] sm:text-xs font-semibold tracking-wide flex items-center justify-center gap-2">
          <span>🌶️ Free Shipping on Orders Above Rs. 499</span>
          <span className="hidden sm:inline">&bull; 100% Stone-Ground & Pure</span>
        </p>
      </div>

      {/* Secondary Filter & Sort Bar matching Screenshot 1 */}
      <FilterSortBar
        totalCount={filteredProducts.length}
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        selectedSort={selectedSort}
        onSelectSort={setSelectedSort}
        inStockOnly={inStockOnly}
        onToggleInStockOnly={setInStockOnly}
      />

      {/* Category Pills (Quick Navigation) */}
      <div className="bg-[#FAF7F2] border-b border-[#E5E0D8] px-3 py-2 overflow-x-auto no-scrollbar flex items-center gap-2">
        <button
          type="button"
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1 text-xs font-bold rounded-sm transition-all whitespace-nowrap ${
            selectedCategory === 'all'
              ? 'variant-box-active'
              : 'variant-box-inactive hover:border-black'
          }`}
        >
          All Items ({products.length})
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1 text-xs font-bold rounded-sm transition-all whitespace-nowrap ${
              selectedCategory === cat
                ? 'variant-box-active'
                : 'variant-box-inactive hover:border-black'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Main 2-Column Border Divided Grid (Matching Screenshot 1) */}
      <main className="max-w-7xl mx-auto">
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 border-l border-t border-[#E5E0D8] bg-white">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onOpenCart={() => setIsCartOpen(true)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 px-4 bg-white border-t border-b border-[#E5E0D8]">
            <div className="w-16 h-16 bg-[#FAF7F2] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#E5E0D8]">
              <ShoppingBag className="w-7 h-7 text-gray-400" />
            </div>
            <h3 className="text-base font-bold text-[#1F1F1F]">No spices found</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">Try clearing your filters or search terms.</p>
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
                setInStockOnly(false);
                setSelectedSort('featured');
              }}
              className="mt-5 px-6 py-2.5 btn-terracotta bg-[#D97736] text-white rounded text-xs font-bold shadow-sm"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Brand Promise Banner */}
        <section className="bg-white border-b border-[#E5E0D8] p-6 sm:p-8 mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center max-w-4xl mx-auto">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#E5E0D8] flex items-center justify-center mb-2.5 text-[#D97736]">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#1F1F1F]">100% Pure & Authentic</h4>
              <p className="text-[11px] text-gray-500 mt-1">No artificial colors, MSG, or preservatives added.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#E5E0D8] flex items-center justify-center mb-2.5 text-[#D97736]">
                <Award className="w-5 h-5" />
              </div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#1F1F1F]">Stone Ground Tradition</h4>
              <p className="text-[11px] text-gray-500 mt-1">Slow roasted spices preserving authentic oils and aroma.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#E5E0D8] flex items-center justify-center mb-2.5 text-[#D97736]">
                <Truck className="w-5 h-5" />
              </div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#1F1F1F]">Direct Fresh Dispatch</h4>
              <p className="text-[11px] text-gray-500 mt-1">Freshly ground batches dispatched straight from the estate.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Floating Checkout Bar above mobile bottom nav */}
      {totalItems > 0 && (
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-20 sm:bottom-24 left-3 right-3 sm:left-4 sm:right-4 z-30"
        >
          <div className="max-w-md mx-auto bg-[#2B2B2B] text-white p-3.5 sm:p-4 rounded-xl shadow-xl flex items-center justify-between gap-4 border border-black/20">
            <div className="flex flex-col pl-2">
              <span className="text-white/70 text-[10px] uppercase font-bold tracking-wider">
                {totalItems} item{totalItems > 1 ? 's' : ''} in Bag
              </span>
              <span className="text-base sm:text-lg font-black text-white">
                Rs. {totalPrice.toFixed(2)}
              </span>
            </div>
            <button
              onClick={() => setIsCartOpen(true)}
              className="btn-terracotta bg-[#D97736] hover:bg-[#C85A17] text-white px-5 sm:px-7 py-2.5 rounded-lg font-bold text-xs uppercase tracking-wider active:scale-95 transition-all flex items-center gap-1.5 shadow-md"
            >
              <span>View Bag</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Checkout Modal */}
      <CheckoutModal isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} />
    </div>
  );
}
